import { el } from "./dom.js";
import { GameClient } from "../game/client.js";
import { InputController, type Tool } from "../game/input.js";
import { Renderer, RENDER_SCALE } from "../game/renderer.js";
import { decodeMap } from "../game/map.js";
import { warmupOverlay } from "./matchLobby.js";
import {
  CHAT_MAX_LENGTH,
  PILLBOX_MAX_ARMOR,
  TANK_DIRECTIONS,
  TANK_MAX_ARMOR,
  TANK_MAX_MINES,
  TANK_MAX_PILLBOXES_CARRY,
  TANK_MAX_SHELLS,
  TANK_MAX_TREES_CAP,
  TEAM_COLORS,
  Theme,
  TILE_SIZE,
  type MatchmadeMode,
  type MatchState,
  type PlayerState,
  type MergedWorldSnapshot,
} from "@webolo/shared";

type HudMode = "modern" | "classic";
type GraphicsMode = "classic" | "modern";

const TOOLS: Array<{ id: Tool; label: string; hotkey: string; color: string }> = [
  { id: "tree",    label: "Farm Trees", hotkey: "1", color: "#7ee787" },
  { id: "road",    label: "Road",       hotkey: "2", color: "#c8a060" },
  { id: "wall",    label: "Building",   hotkey: "3", color: "#aaaaaa" },
  { id: "pillbox", label: "Pillbox",    hotkey: "4", color: "#8ecaf0" },
  { id: "mine",    label: "Lay Mine",   hotkey: "5", color: "#f2c744" },
];

const CONTROLS_LIST: [string, string][] = [
  ["W / ↑",    "Accelerate"],
  ["S / ↓",    "Brake / Reverse"],
  ["A / ←",    "Turn Left"],
  ["D / →",    "Turn Right"],
  ["SPACE",    "Fire"],
  ["]",        "Gunsight Range +"],
  ["[",        "Gunsight Range −"],
  ["TAB",      "Drop Mine"],
  ["B",        "Build (LGM to front)"],
  ["P",        "Drop Pillbox in front"],
  ["1",        "Tool: Farm Trees"],
  ["2",        "Tool: Build Road / Bridge"],
  ["3",        "Tool: Build Building / Boat"],
  ["4",        "Tool: Place or Repair Pillbox"],
  ["5",        "Tool: Lay Mine (via builder)"],
  ["0 / ESC",  "Clear Tool"],
  ["T",        "Open Chat"],
  ["M",        "Toggle Sound"],
  ["N",        "Toggle Player List"],
  ["← / → (dead)", "Spectate Next / Previous Player"],
  ["CLICK MAP","Build / Place at Location"],
];

const GRID_SLOTS = 16;

function loadHudMode(): HudMode {
  // "minimalist" was the previous name for what is now "modern" — fall through.
  const v = localStorage.getItem("webolo-hud");
  return v === "modern" ? "modern" : "classic";
}

function saveHudMode(m: HudMode): void {
  localStorage.setItem("webolo-hud", m);
}

function loadGraphicsMode(): GraphicsMode {
  const v = localStorage.getItem("webolo-graphics");
  return v === "modern" ? "modern" : "classic";
}

function saveGraphicsMode(m: GraphicsMode): void {
  localStorage.setItem("webolo-graphics", m);
}

// Default-on: most players want to see who they're shooting at. The user
// can flip it off via the burger menu when they want a cleaner view.
function loadShowNames(): boolean {
  const v = localStorage.getItem("webolo-show-names");
  return v === null ? true : v === "1";
}

function saveShowNames(on: boolean): void {
  localStorage.setItem("webolo-show-names", on ? "1" : "0");
}

// matchmadeMode: when set, gameScreen renders the Match Lobby overlay during
// the pre-match warmup phase (countdown + chat + ROAM FREE + FFA colour
// picker). The overlay auto-removes when the snapshot's match.phase flips to
// "playing". Undefined for everything except queue-spawned rooms.
export function gameScreen(
  gameId: string,
  team: number,
  onExit: () => void,
  matchmadeMode?: MatchmadeMode,
  // ms epoch the warmup ends, handed back by /api/matchmake. The overlay
  // seeds its countdown from this so the timer paints from the first
  // frame rather than waiting for the first WS snapshot to arrive.
  matchmadeWarmupEndsAt?: number,
): HTMLElement {
  const canvas = el("canvas");
  let hudMode: HudMode = loadHudMode();
  let graphicsMode: GraphicsMode = loadGraphicsMode();

  // ── shared state ────────────────────────────────────────────────────────────
  let selfSnapshot: PlayerState | null = null;
  let lastSnap: MergedWorldSnapshot | null = null;
  let lastMatch: MatchState | null = null;
  let mapInfoText = "";
  let statusText  = "connecting…";
  // playerId the dead-spectator camera is following; null = follow self.
  let spectateId: number | null = null;

  // ── game engine ─────────────────────────────────────────────────────────────
  const renderer = new Renderer(canvas);
  const input    = new InputController();
  const client   = new GameClient();

  input.attachCanvas(canvas);
  canvas.tabIndex = 0;
  canvas.addEventListener("click", () => { canvas.focus(); renderer.unlockAudio(); });
  canvas.addEventListener("keydown", () => renderer.unlockAudio(), { once: true });

  // ── controls modal ───────────────────────────────────────────────────────────
  const controlsModal = el("div", { class: "ctrl-modal hidden" },
    el("div", { class: "ctrl-backdrop", onclick: closeControls }),
    el("div", { class: "ctrl-card" },
      el("div", { class: "ctrl-card-head" },
        el("span", { class: "ctrl-card-title" }, "CONTROLS"),
        el("button", { class: "ctrl-close", onclick: closeControls }, "×"),
      ),
      el("table", { class: "ctrl-table" },
        el("tbody", {},
          ...CONTROLS_LIST.map(([key, action]) =>
            el("tr", {},
              el("td", { class: "ctrl-key" }, key),
              el("td", { class: "ctrl-action" }, action),
            ),
          ),
        ),
      ),
    ),
  );

  function openControls(): void {
    controlsModal.classList.remove("hidden");
    closeBurger();
  }

  function closeControls(): void {
    controlsModal.classList.add("hidden");
  }

  // ── scoreboard modal ─────────────────────────────────────────────────────────
  const scoreBody = el("div", { class: "score-body" });

  const scoreboardModal = el("div", { class: "score-modal hidden" },
    el("div", { class: "score-backdrop", onclick: () => closeScoreboard() }),
    el("div", { class: "score-card" },
      el("div", { class: "score-card-head" },
        el("span", { class: "score-card-title" }, "PLAYERS"),
        el("button", { class: "score-close", onclick: () => closeScoreboard() }, "×"),
      ),
      scoreBody,
    ),
  );

  // Inline colour swatch beside each player name in the scoreboard. FFA
  // tanks use the picked colorIndex (matches their in-world hull). Team
  // tanks use the team palette swatch. FutureWar/Skynet rooms suppress
  // per-player FFA dots for non-self entries so the enemy roster reads as
  // one uniform Skynet army; self keeps their pick. Returns a thin <span>
  // so the caller can append text directly afterwards.
  function colorDot(p: PlayerState): HTMLElement {
    const selfId = client.getSelfId();
    const isSelf = p.id === selfId;
    const isFutureWar = Renderer.currentTheme === Theme.FutureWar;
    let color: string;
    if (p.team > 0) {
      color = TEAM_COLORS[p.team] ?? "#888";
    } else if (
      p.colorIndex !== undefined &&
      p.colorIndex >= 0 &&
      p.colorIndex < TEAM_COLORS.length &&
      (isSelf || !isFutureWar)
    ) {
      color = TEAM_COLORS[p.colorIndex];
    } else {
      color = "#3a6ac0";
    }
    return el("span", { class: "score-dot", style: `background:${color}` });
  }

  function buildScoreRows(snap: MergedWorldSnapshot): HTMLElement {
    const selfId   = client.getSelfId();
    const hasTeams = snap.players.some((p) => p.team > 0);
    if (hasTeams) {
      const teams = new Map<number, { kills: number; deaths: number; players: PlayerState[] }>();
      for (const p of snap.players) {
        const e = teams.get(p.team) ?? { kills: 0, deaths: 0, players: [] as PlayerState[] };
        e.kills  += p.kills;
        e.deaths += p.deaths;
        e.players.push(p);
        teams.set(p.team, e);
      }
      const rows: HTMLElement[] = [];
      for (const [, t] of [...teams.entries()].sort((a, b) => b[1].kills - a[1].kills)) {
        const label = t.players[0].team === 0 ? "No team" : `Team ${t.players[0].team}`;
        const color = TEAM_COLORS[t.players[0].team] ?? "#ccc";
        rows.push(el("tr", { class: "score-team-row" },
          el("td", { style: `color:${color}; font-weight:600;` }, label),
          el("td", { class: "score-num" }, String(t.kills)),
          el("td", { class: "score-num" }, String(t.deaths)),
        ));
        for (const p of [...t.players].sort((a, b) => b.kills - a.kills)) {
          rows.push(el("tr", { class: p.id === selfId ? "score-self" : "" },
            el("td", { style: "padding-left:1.2rem;" }, colorDot(p), p.name + (p.alive ? "" : " †")),
            el("td", { class: "score-num" }, String(p.kills)),
            el("td", { class: "score-num" }, String(p.deaths)),
          ));
        }
      }
      return el("table", { class: "score-table" },
        el("thead", {}, el("tr", {},
          el("th", {}, "Team / Player"),
          el("th", { class: "score-num" }, "K"),
          el("th", { class: "score-num" }, "D"),
        )),
        el("tbody", {}, ...rows),
      );
    } else {
      const sorted = [...snap.players].sort((a, b) => b.kills - a.kills || a.deaths - b.deaths);
      return el("table", { class: "score-table" },
        el("thead", {}, el("tr", {},
          el("th", {}, "Player"),
          el("th", { class: "score-num" }, "K"),
          el("th", { class: "score-num" }, "D"),
        )),
        el("tbody", {},
          ...sorted.map((p) =>
            el("tr", { class: p.id === selfId ? "score-self" : "" },
              el("td", {}, colorDot(p), p.name + (p.alive ? "" : " †")),
              el("td", { class: "score-num" }, String(p.kills)),
              el("td", { class: "score-num" }, String(p.deaths)),
            ),
          ),
        ),
      );
    }
  }

  function openScoreboard(): void {
    if (lastSnap) scoreBody.replaceChildren(buildScoreRows(lastSnap));
    scoreboardModal.classList.remove("hidden");
  }

  function closeScoreboard(): void {
    scoreboardModal.classList.add("hidden");
  }

  function toggleScoreboard(): void {
    if (scoreboardModal.classList.contains("hidden")) openScoreboard();
    else closeScoreboard();
  }

  // ── burger menu ──────────────────────────────────────────────────────────────
  let burgerOpen = false;

  const bmStatus = el("span", { class: "bm-stat-val" }, "connecting…");
  const bmMap    = el("span", { class: "bm-stat-val" }, "—");
  const bmTimer  = el("span", { class: "bm-stat-val" }, "--:--");
  const bmKD     = el("span", { class: "bm-stat-val" }, "—");

  const hudMinBtn = el("button", { class: "bm-iface-btn", onclick: () => setHudMode("modern")  }, "Modern");
  const hudClsBtn = el("button", { class: "bm-iface-btn", onclick: () => setHudMode("classic") }, "Classic");

  const gfxClsBtn = el("button", { class: "bm-iface-btn", onclick: () => setGraphicsMode("classic") }, "Classic");
  const gfxModBtn = el("button", { class: "bm-iface-btn", onclick: () => setGraphicsMode("modern")  }, "Modern");

  // Name-label toggle. Initialised from localStorage so the choice
  // sticks across reloads; mutates Renderer.showTankNames which both
  // tank draw helpers consult per frame.
  let showNames = loadShowNames();
  Renderer.showTankNames = showNames;
  const namesOnBtn  = el("button", { class: "bm-iface-btn", onclick: () => setShowNames(true)  }, "On");
  const namesOffBtn = el("button", { class: "bm-iface-btn", onclick: () => setShowNames(false) }, "Off");
  function setShowNames(on: boolean): void {
    showNames = on;
    Renderer.showTankNames = on;
    saveShowNames(on);
    namesOnBtn.classList.toggle("active", on);
    namesOffBtn.classList.toggle("active", !on);
  }

  // Graphics section is only shown in dev builds while the modern tile
  // sheet is still being polished.
  const gfxSection: Node[] = import.meta.env.DEV ? [
    el("div", { class: "bm-section-lbl" }, "Graphics"),
    el("div", { class: "bm-iface-row" }, gfxClsBtn, gfxModBtn),
  ] : [];

  const burgerMenu = el("div", { class: "burger-menu hidden" },
    el("div", { class: "bm-stats" },
      el("div", { class: "bm-stat-row" }, el("span", { class: "bm-stat-key" }, "Status"), bmStatus),
      el("div", { class: "bm-stat-row" }, el("span", { class: "bm-stat-key" }, "Map"),    bmMap),
      el("div", { class: "bm-stat-row" }, el("span", { class: "bm-stat-key" }, "Time"),   bmTimer),
      el("div", { class: "bm-stat-row" }, el("span", { class: "bm-stat-key" }, "K / D"),  bmKD),
    ),
    el("div", { class: "bm-divider" }),
    el("button", { class: "bm-action", onclick: openControls }, "Controls"),
    el("div", { class: "bm-section-lbl" }, "Tank Interface"),
    el("div", { class: "bm-iface-row" }, hudMinBtn, hudClsBtn),
    el("div", { class: "bm-section-lbl" }, "Names"),
    el("div", { class: "bm-iface-row" }, namesOnBtn, namesOffBtn),
    ...gfxSection,
    el("div", { class: "bm-divider" }),
    el("button", {
      class: "bm-leave",
      onclick: () => { client.disconnect(); onExit(); },
    }, "Leave Game"),
  );

  const burgerBtn = el("button", {
    class: "burger-btn",
    onclick: (e: Event) => { e.stopPropagation(); toggleBurger(); },
  }, "☰");

  function toggleBurger(): void {
    burgerOpen = !burgerOpen;
    burgerMenu.classList.toggle("hidden", !burgerOpen);
    if (burgerOpen) refreshBurgerStats();
  }

  function closeBurger(): void {
    burgerOpen = false;
    burgerMenu.classList.add("hidden");
  }

  function refreshBurgerStats(): void {
    bmStatus.textContent = statusText;
    bmMap.textContent    = mapInfoText || "—";
    if (lastMatch) bmTimer.textContent = matchTimerText(lastMatch);
    if (selfSnapshot) {
      bmKD.textContent =
        `K ${selfSnapshot.kills} · D ${selfSnapshot.deaths}` +
        (selfSnapshot.alive ? "" : " · DEAD");
    }
  }

  // ── top bar ──────────────────────────────────────────────────────────────────
  // TIME and K/D live in the bottom HUD (cls-right) and the burger menu —
  // showing them in the top bar too made the chrome feel busy.
  const tbScore = el("span", { class: "tb-stat tb-score" });
  tbScore.style.display = "none";

  const muteBtn = el("button", {
    class: "mute-btn",
    title: "Toggle sound (M)",
    onclick: () => {
      renderer.unlockAudio();
      const muted = renderer.toggleMute();
      muteBtn.textContent = muted ? "🔇" : "🔊";
      muteBtn.classList.toggle("muted", muted);
    },
  }, "🔊");

  const topBar = el("div", { class: "game-top-bar" },
    el("div", { class: "gtb-brand" },
      el("div", { class: "gtb-logo" },
        el("span", { class: "logo-we" }, "WE"),
        el("span", { class: "logo-bolo" }, "BOLO"),
      ),
    ),
    el("div", { class: "gtb-stats" }, tbScore),
    el("div", { class: "gtb-right" },
      muteBtn,
      burgerBtn,
      burgerMenu,
    ),
  );

  function refreshTopBarStats(): void {
    if (lastSnap) {
      const teams = new Map<number, number>();
      for (const p of lastSnap.players) {
        if (p.team > 0) teams.set(p.team, (teams.get(p.team) ?? 0) + p.kills);
      }
      if (teams.size > 0) {
        const myTeam = selfSnapshot?.team ?? 0;
        const sorted = [...teams.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0]);
        tbScore.replaceChildren(
          ...sorted.flatMap(([t, k], i) => {
            const seg = el("span",
              { class: "tb-team" + (t === myTeam ? " tb-team-self" : "") },
              el("span", { class: "tb-key", style: `color: ${TEAM_COLORS[t] ?? "#ccc"};` }, `T${t}`),
              el("span", { class: "tb-val" }, String(k)),
            );
            return i === 0 ? [seg] : [el("span", { class: "tb-sep" }, "·"), seg];
          }),
        );
        tbScore.style.display = "";
      } else {
        tbScore.style.display = "none";
      }
    }
  }

  // ── minimalist inventory ─────────────────────────────────────────────────────
  const armorBar   = hbar("#ff6b6b");
  const shellsBar  = hbar("#ffe066");
  const minesBar   = hbar("#ff9f4d");
  const treesBar   = hbar("#7ee787");
  const pillboxBar = hbar("#8ecaf0");

  const minToolBtns: Record<Tool, HTMLElement> = {} as Record<Tool, HTMLElement>;
  const minToolbar = el("div", { class: "toolbar" });
  for (const t of TOOLS) {
    const btn = el("button", {
      class: "tool-btn",
      title: `${t.label} (${t.hotkey})`,
      onclick: () => input.setTool(input.getTool() === t.id ? "none" : t.id),
    },
      el("span", { class: "tool-key"   }, t.hotkey),
      el("span", { class: "tool-label" }, t.label),
    );
    minToolBtns[t.id] = btn;
    minToolbar.append(btn);
  }

  // Slots in the modern inventory that receive the shared status grids
  // when modern mode is active. classic mode pulls them back into clsRight.
  const modTankSlot = el("div", { class: "mod-status-slot" });
  const modPillSlot = el("div", { class: "mod-status-slot" });
  const modBaseSlot = el("div", { class: "mod-status-slot" });

  const minInventory = el("div", { class: "game-inventory mod-panel" },
    el("div", { class: "mod-panel-head" },
      el("span", { class: "mod-panel-ttl" }, "TANK"),
      el("span", { class: "mod-panel-sub" }, "ACTIVE"),
    ),
    el("div", { class: "mod-panel-body" },
      el("div", { class: "mod-section" },
        el("div", { class: "mod-section-ttl" }, "SUPPLIES"),
        bundle("Armor",   armorBar.wrap),
        bundle("Shells",  shellsBar.wrap),
        bundle("Mines",   minesBar.wrap),
        bundle("Trees",   treesBar.wrap),
        bundle("Pillbox", pillboxBar.wrap),
      ),
      el("div", { class: "mod-section" },
        el("div", { class: "mod-section-ttl" }, "TOOLS"),
        minToolbar,
      ),
      el("div", { class: "mod-section" },
        el("div", { class: "mod-section-ttl" }, "FIELD"),
        el("div", { class: "mod-status-row" },
          el("span", { class: "mod-status-lbl" }, "TANKS"),
          modTankSlot,
        ),
        el("div", { class: "mod-status-row" },
          el("span", { class: "mod-status-lbl" }, "PILLS"),
          modPillSlot,
        ),
        el("div", { class: "mod-status-row" },
          el("span", { class: "mod-status-lbl" }, "BASES"),
          modBaseSlot,
        ),
      ),
    ),
    el("div", { class: "rv-bl" }),
    el("div", { class: "rv-br" }),
  );

  // ── classic left panel (build selector) ─────────────────────────────────────
  // Mirrors winbolo's 5 LGM action slots in order: Tree, Road, Building,
  // Pillbox, Mine. Road/Building also build on water (bridge/boat).
  const clsToolBtns: Record<Tool, HTMLElement> = {} as Record<Tool, HTMLElement>;
  const clsLeft = el("div", { class: "cls-left" });

  const CLS_BUILD_ORDER: Array<{ tool: Tool | null; label: string; hotkey: string; icon: string; iconColor: string }> = [
    { tool: "tree",    label: "Farm Trees", hotkey: "1", icon: "tree",    iconColor: "#1a8a35" },
    { tool: "road",    label: "Road",       hotkey: "2", icon: "road",    iconColor: "#caa055" },
    { tool: "wall",    label: "Building",   hotkey: "3", icon: "wall",    iconColor: "#c8a868" },
    { tool: "pillbox", label: "Pillbox",    hotkey: "4", icon: "pillbox", iconColor: "#5fa8d8" },
    { tool: "mine",    label: "Lay Mine",   hotkey: "5", icon: "mine",    iconColor: "#e8c038" },
  ];

  for (const slot of CLS_BUILD_ORDER) {
    const iconWrap = el("div", { class: `cls-tool-icon cls-icon-${slot.icon}`, style: `color:${slot.iconColor};` });
    const btn = el("button", {
      class: "cls-tool-box" + (slot.tool === null ? " cls-tool-disabled" : ""),
      title: slot.tool === null ? `${slot.label} (auto-laid via TAB)` : `${slot.label} (${slot.hotkey})`,
      ...(slot.tool !== null
        ? { onclick: () => input.setTool(input.getTool() === slot.tool ? "none" : slot.tool!) }
        : {}),
    }, iconWrap);
    // Each row in the left column: a small red selection dot outside-left + the box.
    const row = el("div", { class: "cls-tool-row" },
      el("div", { class: "cls-tool-dot" }),
      btn,
    );
    if (slot.tool !== null) clsToolBtns[slot.tool] = row;
    clsLeft.append(row);
  }

  // ── classic right panel (status + bars) ─────────────────────────────────────
  function makeGrid(kind: "tank" | "pill" | "base"): { root: HTMLElement; dots: HTMLElement[] } {
    const dots: HTMLElement[] = [];
    const root = el("div", { class: `cls-status-grid cls-grid-${kind}` });
    for (let i = 0; i < GRID_SLOTS; i++) {
      const d = el("div", { class: `cls-dot cls-dot-${kind} cls-dot-pos-${i}` });
      dots.push(d);
      root.append(d);
    }
    root.append(el("div", { class: `cls-status-center cls-center-${kind}` }));
    return { root, dots };
  }

  const tankGrid = makeGrid("tank");
  const pillGrid = makeGrid("pill");
  const baseGrid = makeGrid("base");

  const clsShellsBar = vbar("#ffe066", "S");
  const clsMinesBar  = vbar("#ff9f4d", "M");
  const clsArmorBar  = vbar("#ff6b6b", "A");
  const clsTreesBar  = vbar("#7ee787", "T");

  const clsKills  = el("span", { class: "cls-kd-num" }, "0");
  const clsDeaths = el("span", { class: "cls-kd-num" }, "0");

  // Winbolo right side has TWO columns:
  //   inner: 3 stacked status panels (TANKS / PILLBOXES / BASES) — sunken
  //          bevels each with a 6×3 ownership grid + central type icon.
  //   outer (narrow): K and D rows at top, vertical supply bars filling the
  //          middle, and a small icons row at bottom — exactly per
  //          STATUS_KILLS_TOP / STATUS_DEATHS_TOP / STATUS_TANK_BARS_TOP.
  // Panels are named vars so the grid roots can be reparented into the
  // modern HUD's left column when the user switches modes.
  const clsTankPanel = el("div", { class: "cls-status-panel" });
  const clsPillPanel = el("div", { class: "cls-status-panel" });
  const clsBasePanel = el("div", { class: "cls-status-panel" });
  const clsRight = el("div", { class: "cls-right" },
    el("div", { class: "cls-right-inner" }, clsTankPanel, clsPillPanel, clsBasePanel),
    el("div", { class: "cls-right-outer" },
      el("div", { class: "cls-kd-row" },
        el("span", { class: "cls-kd-icon cls-kd-kill"  }),
        clsKills,
      ),
      el("div", { class: "cls-kd-row" },
        el("span", { class: "cls-kd-icon cls-kd-death" }),
        clsDeaths,
      ),
      el("div", { class: "cls-vbars" },
        clsShellsBar.root, clsMinesBar.root, clsArmorBar.root, clsTreesBar.root,
      ),
      el("div", { class: "cls-mini-icons" },
        el("span", { class: "cls-mini cls-mini-shells", title: "Shells" }),
        el("span", { class: "cls-mini cls-mini-mines",  title: "Mines"  }),
        el("span", { class: "cls-mini cls-mini-armor",  title: "Armor"  }),
        el("span", { class: "cls-mini cls-mini-trees",  title: "Trees"  }),
      ),
    ),
  );

  // ── chat ─────────────────────────────────────────────────────────────────────
  const chatLog = el("div", { class: "chat-log" });
  const chatInput = el("input", {
    type: "text",
    class: "chat-input",
    maxlength: CHAT_MAX_LENGTH,
    placeholder: "Press T to chat…",
  }) as HTMLInputElement;

  const minChat  = el("div", { class: "chat mod-panel" },
    el("div", { class: "mod-panel-head" },
      el("span", { class: "mod-panel-ttl" }, "COMMS"),
      el("span", { class: "mod-panel-sub" }, "T to type"),
    ),
    el("div", { class: "mod-chat-body" }),
    el("div", { class: "mod-chat-foot" }),
    el("div", { class: "rv-bl" }),
    el("div", { class: "rv-br" }),
  );
  const clsBottom = el("div", { class: "cls-bottom" });

  // Canvas host is a flex slot in either modern's mod-row or classic's cls-row.
  const canvasHost = el("div", { class: "canvas-host" }, canvas);

  // Modern shell: lobby-styled panels flanking the canvas as flex siblings
  // (no overlay). The mod-row holds [inventory] [canvas-host] [chat].
  const modRow = el("div", { class: "mod-row" });

  // Classic shell: grey chrome wrapping the canvas with bevelled side panels
  // and a bottom message strip — matches the original winbolo window layout.
  const clsRow   = el("div", { class: "cls-row" });
  const clsShell = el("div", { class: "cls-shell" }, clsRow, clsBottom);

  chatInput.addEventListener("keydown", (e) => {
    const ev = e as KeyboardEvent;
    ev.stopPropagation();
    if (ev.key === "Enter") {
      const text = chatInput.value.trim();
      if (text) client.sendChat(text);
      chatInput.value = "";
      chatInput.blur();
      canvas.focus();
      ev.preventDefault();
    } else if (ev.key === "Escape") {
      chatInput.value = "";
      chatInput.blur();
      ev.preventDefault();
    }
  });

  window.addEventListener("keydown", (e) => {
    if ((e.target as HTMLElement)?.tagName === "INPUT") return;
    if (e.key === "t" || e.key === "T") {
      chatInput.focus();
      e.preventDefault();
    }
    if (e.key === "m" || e.key === "M") {
      renderer.unlockAudio();
      const muted = renderer.toggleMute();
      muteBtn.textContent = muted ? "🔇" : "🔊";
      muteBtn.classList.toggle("muted", muted);
      e.preventDefault();
    }
  });

  // ── match banner ─────────────────────────────────────────────────────────────
  const matchBanner = el("div", { class: "match-banner hidden" });

  // ── siege HUD (big top-centre countdown / round display) ──────────────────────
  const siegeHud = el("div", { class: "siege-hud hidden" });

  // ── battle-royale HUD — reuses the siege HUD styling (same top-centre slot)
  // for the stage / fog-countdown / players-left display.
  const battleRoyaleHud = el("div", { class: "siege-hud br-hud hidden" });

  // ── spectate banner — shown while the local tank is dead and the camera is
  // following another (live) player. ← / → cycle the target.
  const spectateBanner = el("div", { class: "spectate-banner hidden" });

  // ── HUD mode switching ───────────────────────────────────────────────────────
  function updateHudButtons(): void {
    hudMinBtn.classList.toggle("active", hudMode === "modern");
    hudClsBtn.classList.toggle("active", hudMode === "classic");
  }

  function applyHudMode(): void {
    wrap.setAttribute("data-hud", hudMode);

    if (hudMode === "modern") {
      // Tear down classic
      clsShell.remove();
      // Move shared status grids into the modern left column slots
      modTankSlot.append(tankGrid.root);
      modPillSlot.append(pillGrid.root);
      modBaseSlot.append(baseGrid.root);
      // Build modern row: [inventory] [canvas-host] [chat] — flex siblings,
      // panels wrap the canvas, no overlap.
      modRow.replaceChildren(minInventory, canvasHost, minChat);
      canvasArea.append(modRow);
      // Match banner is centred over the canvas
      canvasHost.append(matchBanner, siegeHud, battleRoyaleHud, spectateBanner);
      // Chat children always live in the active container.
      const chatBody = minChat.querySelector(".mod-chat-body") as HTMLElement;
      const chatFoot = minChat.querySelector(".mod-chat-foot") as HTMLElement;
      chatBody.append(chatLog);
      chatFoot.append(chatInput);
    } else {
      // Tear down modern
      modRow.remove();
      // Pull status grids back into the classic right column
      clsTankPanel.append(tankGrid.root);
      clsPillPanel.append(pillGrid.root);
      clsBasePanel.append(baseGrid.root);
      // Build classic layout: [left] [canvas-host] [right] / bottom
      clsRow.replaceChildren(clsLeft, canvasHost, clsRight);
      clsBottom.replaceChildren(chatLog, chatInput);
      canvasArea.append(clsShell);
      canvasHost.append(matchBanner, siegeHud, battleRoyaleHud, spectateBanner);
    }
  }

  function setHudMode(mode: HudMode): void {
    hudMode = mode;
    saveHudMode(mode);
    applyHudMode();
    updateHudButtons();
    closeBurger();
  }

  function updateGraphicsButtons(): void {
    gfxClsBtn.classList.toggle("active", graphicsMode === "classic");
    gfxModBtn.classList.toggle("active", graphicsMode === "modern");
  }

  function setGraphicsMode(mode: GraphicsMode): void {
    graphicsMode = mode;
    saveGraphicsMode(mode);
    renderer.setGraphicsMode(mode);
    updateGraphicsButtons();
    closeBurger();
  }

  // ── toolbar sync ─────────────────────────────────────────────────────────────
  function updateToolbars(active: Tool): void {
    for (const t of TOOLS) {
      minToolBtns[t.id]?.classList.toggle("active", active === t.id);
      clsToolBtns[t.id]?.classList.toggle("cls-tool-active", active === t.id);
    }
  }

  input.onToolChange = updateToolbars;
  updateToolbars("none");

  // ── snapshot → HUD ───────────────────────────────────────────────────────────
  function updateFromSnapshot(snap: MergedWorldSnapshot): void {
    const selfId = client.getSelfId();
    const self   = snap.players.find((p) => p.id === selfId) ?? null;
    selfSnapshot = self;
    lastSnap     = snap;
    lastMatch    = snap.match;

    if (self) {
      renderer.setSelfTeam(self.team);

      // minimalist bars
      armorBar.set(  self.armor    / TANK_MAX_ARMOR,            `${self.armor}/${TANK_MAX_ARMOR}`);
      shellsBar.set( self.shells   / TANK_MAX_SHELLS,           `${self.shells}/${TANK_MAX_SHELLS}`);
      minesBar.set(  self.mines    / TANK_MAX_MINES,            `${self.mines}/${TANK_MAX_MINES}`);
      treesBar.set(  self.trees    / TANK_MAX_TREES_CAP,        `${self.trees}/${TANK_MAX_TREES_CAP}`);
      pillboxBar.set(self.pillboxes / TANK_MAX_PILLBOXES_CARRY, `${self.pillboxes}/${TANK_MAX_PILLBOXES_CARRY}`);

      // classic bars
      clsShellsBar.set(self.shells   / TANK_MAX_SHELLS);
      clsMinesBar.set( self.mines    / TANK_MAX_MINES);
      clsArmorBar.set( self.armor    / TANK_MAX_ARMOR);
      clsTreesBar.set( self.trees    / TANK_MAX_TREES_CAP);

      clsKills.textContent  = String(self.kills);
      clsDeaths.textContent = String(self.deaths);
    }

    const selfTeam = self?.team ?? 0;

    const STATE_CLASSES = ["cls-st-empty", "cls-st-self", "cls-st-ally", "cls-st-enemy", "cls-st-neutral", "cls-st-dead"];
    const setState = (dot: HTMLElement, state: string, dead = false) => {
      for (const c of STATE_CLASSES) dot.classList.remove(c);
      dot.classList.add(state);
      dot.classList.toggle("cls-st-dead", dead);
    };

    // tank status grid
    for (let i = 0; i < GRID_SLOTS; i++) {
      const p   = snap.players[i];
      const dot = tankGrid.dots[i];
      if (!p) {
        setState(dot, "cls-st-empty");
        dot.title = "";
      } else if (p.id === selfId) {
        setState(dot, "cls-st-self", !p.alive);
        dot.title = p.name + " (you)";
      } else if (selfTeam > 0 && p.team === selfTeam) {
        setState(dot, "cls-st-ally", !p.alive);
        dot.title = p.name;
      } else {
        setState(dot, "cls-st-enemy", !p.alive);
        dot.title = p.name;
      }
    }

    // pillbox status grid. mines/pillboxes/bases are delta-encoded over the
    // wire (protocol v4) and merged back to complete lists in GameClient, so
    // they're always present here; `?? []` only satisfies the optional type.
    const pillboxes = snap.pillboxes ?? [];
    const bases = snap.bases ?? [];
    for (let i = 0; i < GRID_SLOTS; i++) {
      const pb  = pillboxes[i];
      const dot = pillGrid.dots[i];
      if (!pb) {
        setState(dot, "cls-st-empty");
        dot.title = "";
      } else if (pb.owner === selfId || (selfTeam > 0 && pb.team === selfTeam)) {
        setState(dot, "cls-st-ally");
        dot.title = "Pillbox (yours)";
      } else if (pb.owner === 0 && pb.team === 0) {
        setState(dot, "cls-st-neutral");
        dot.title = "Pillbox (neutral)";
      } else {
        setState(dot, "cls-st-enemy");
        dot.title = "Pillbox (enemy)";
      }
    }

    // base status grid
    for (let i = 0; i < GRID_SLOTS; i++) {
      const base = bases[i];
      const dot  = baseGrid.dots[i];
      if (!base) {
        setState(dot, "cls-st-empty");
        dot.title = "";
      } else if (base.owner === selfId || (selfTeam > 0 && base.team === selfTeam)) {
        setState(dot, "cls-st-ally");
        dot.title = "Base (yours)";
      } else if (base.owner === 0 && base.team === 0) {
        setState(dot, "cls-st-neutral");
        dot.title = "Base (neutral)";
      } else {
        setState(dot, "cls-st-enemy");
        dot.title = "Base (enemy)";
      }
    }

    // Siege rooms run their own lifecycle (the timed match never ends), so the
    // normal end-of-match scoreboard is suppressed in favour of the siege HUD.
    if (snap.siege) {
      battleRoyaleHud.classList.add("hidden");
      hideMatchBanner();
      updateSiegeHud(snap.siege);
    } else if (snap.battleRoyale) {
      siegeHud.classList.add("hidden");
      if (snap.match.phase === "ended") {
        battleRoyaleHud.classList.add("hidden");
        showMatchBanner(snap);
      } else {
        hideMatchBanner();
        updateBattleRoyaleHud(snap.battleRoyale, snap);
      }
    } else {
      siegeHud.classList.add("hidden");
      battleRoyaleHud.classList.add("hidden");
      if (snap.match.phase === "ended") showMatchBanner(snap);
      else hideMatchBanner();
    }

    refreshTopBarStats();
    updateSpectate();
  }

  // Live players the dead-spectator camera can cycle through (everyone alive
  // except the local player), in a stable order.
  function livePlayers(): PlayerState[] {
    const selfId = client.getSelfId();
    return (lastSnap?.players ?? [])
      .filter((p) => p.alive && p.id !== selfId)
      .sort((a, b) => a.id - b.id);
  }

  // Reconcile the spectate camera with the latest snapshot: clear it while the
  // local tank is alive; otherwise lock onto a live player (keeping the current
  // target if it's still alive) and show the banner. Re-runs every snapshot so
  // a target that dies is replaced automatically.
  function updateSpectate(): void {
    if (selfSnapshot?.alive !== false) {
      if (spectateId !== null) { spectateId = null; renderer.setSpectateId(null); }
      spectateBanner.classList.add("hidden");
      return;
    }
    const live = livePlayers();
    if (live.length === 0) {
      spectateId = null;
      renderer.setSpectateId(null);
      spectateBanner.classList.add("hidden");
      return;
    }
    if (spectateId === null || !live.some((p) => p.id === spectateId)) {
      spectateId = live[0].id;
    }
    renderer.setSpectateId(spectateId);
    const name = live.find((p) => p.id === spectateId)?.name ?? "";
    spectateBanner.textContent = `SPECTATING ${name.toUpperCase()}   ·   ← →  CHANGE`;
    spectateBanner.classList.remove("hidden");
  }

  // Step the spectate target while dead. No-op while alive (the arrows keep
  // driving the tank).
  function cycleSpectate(dir: 1 | -1): void {
    if (selfSnapshot?.alive !== false) return;
    const live = livePlayers();
    if (live.length === 0) return;
    let idx = live.findIndex((p) => p.id === spectateId);
    if (idx < 0) idx = 0;
    idx = (idx + dir + live.length) % live.length;
    spectateId = live[idx].id;
    updateSpectate();
  }

  // Drives the big top-centre siege display from the snapshot's siege state.
  // prevSiegePhase + attackFlashUntil hold the "YOU'RE UNDER ATTACK!" banner on
  // screen for a beat when prep flips to the first wave (otherwise the prep→wave
  // transition is a single tick and the alarm would be unreadable).
  let prevSiegePhase: string | null = null;
  let attackFlashUntil = 0;
  function updateSiegeHud(s: NonNullable<MergedWorldSnapshot["siege"]>): void {
    siegeHud.classList.remove("hidden");
    const now = Date.now();
    const secs = Math.max(0, Math.ceil((s.phaseEndsAt - now) / 1000));
    if (prevSiegePhase === "prep" && s.phase === "wave") attackFlashUntil = now + 2000;
    prevSiegePhase = s.phase;
    const flashing = now < attackFlashUntil;
    const underAttack = (s.phase === "prep" && secs <= 0) || flashing;
    siegeHud.classList.toggle("siege-alarm", underAttack || s.phase === "gameover");

    const parts: HTMLElement[] = [];
    if (underAttack) {
      parts.push(el("div", { class: "siege-label" }, "GET READY"));
      parts.push(el("div", { class: "siege-big siege-big-alarm" }, "YOU'RE UNDER ATTACK!"));
    } else if (s.phase === "prep") {
      parts.push(el("div", { class: "siege-label" }, "GET READY"));
      parts.push(el("div", { class: "siege-count" }, String(secs)));
      parts.push(el("div", { class: "siege-sub" }, "Grab every resource you can before the assault"));
    } else if (s.phase === "wave") {
      const clock = `${Math.floor(secs / 60)}:${(secs % 60).toString().padStart(2, "0")}`;
      parts.push(el("div", { class: "siege-big" }, `ROUND ${s.round}`));
      parts.push(el("div", { class: "siege-sub" },
        `${s.enemiesAlive} ${s.enemiesAlive === 1 ? "enemy" : "enemies"} left · ${clock}`));
    } else if (s.phase === "break") {
      parts.push(el("div", { class: "siege-label" }, `ROUND ${s.round + 1} INCOMING`));
      parts.push(el("div", { class: "siege-count" }, String(secs)));
    } else {
      parts.push(el("div", { class: "siege-big siege-big-over" }, "GAME OVER"));
      parts.push(el("div", { class: "siege-sub" },
        `You reached Round ${s.round} · Best: Round ${s.bestRound}`));
      parts.push(el("div", { class: "siege-sub" }, `New run in ${secs}s`));
    }
    siegeHud.replaceChildren(...parts);
  }

  // Drives the top-centre battle-royale display: current fog stage, the
  // countdown to the next shrink, the survivor headcount, and a loud warning
  // when the local tank is caught outside the safe circle.
  function updateBattleRoyaleHud(
    b: NonNullable<MergedWorldSnapshot["battleRoyale"]>,
    snap: MergedWorldSnapshot,
  ): void {
    battleRoyaleHud.classList.remove("hidden");
    const now = Date.now();
    const parts: HTMLElement[] = [];

    if (b.phase === "waiting") {
      battleRoyaleHud.classList.remove("siege-alarm");
      parts.push(el("div", { class: "siege-label" }, "BATTLE ROYALE"));
      parts.push(el("div", { class: "siege-sub" }, `${b.alive} tanks · the fog is coming`));
      battleRoyaleHud.replaceChildren(...parts);
      return;
    }

    // Is our own tank stranded in the fog right now?
    const self = snap.players.find((p) => p.id === client.getSelfId());
    let inFog = false;
    if (self && self.alive) {
      const dx = self.x - b.zoneX;
      const dy = self.y - b.zoneY;
      inFog = dx * dx + dy * dy > b.zoneRadius * b.zoneRadius;
    }
    battleRoyaleHud.classList.toggle("siege-alarm", inFog || b.stage >= b.stageCount);

    const secs = Math.max(0, Math.ceil((b.stageEndsAt - now) / 1000));
    const clock = `${Math.floor(secs / 60)}:${(secs % 60).toString().padStart(2, "0")}`;

    parts.push(el("div", { class: "siege-label" },
      `STAGE ${b.stage}/${b.stageCount} · ${b.alive} LEFT`));
    if (inFog) {
      parts.push(el("div", { class: "siege-big siege-big-alarm" }, "GET TO THE ZONE!"));
      parts.push(el("div", { class: "siege-sub" }, `Losing ${b.drainPerSec.toFixed(1)} armour/sec in the fog`));
    } else {
      parts.push(el("div", { class: "siege-big" }, "FOG CLOSING"));
      parts.push(el("div", { class: "siege-sub" },
        `Next shrink in ${clock} · ${b.drainPerSec.toFixed(1)} dmg/sec outside`));
    }
    battleRoyaleHud.replaceChildren(...parts);
  }

  // ── client callbacks ─────────────────────────────────────────────────────────
  client.onOpen  = () => { statusText = "connected";          bmStatus.textContent = statusText; };
  client.onClose = (r) => { statusText = `disconnected: ${r}`; bmStatus.textContent = statusText; };
  client.onMap   = (desc, selfId) => {
    const map = decodeMap(desc);
    renderer.setMap(map);
    renderer.setSelfId(selfId);
    mapInfoText      = `${map.name} · ${map.width}×${map.height}`;
    bmMap.textContent = mapInfoText;
  };
  client.onTiles    = (u)       => renderer.patchTiles(u);

  // Match Lobby overlay — present only for matchmade FFA/Teams rooms during
  // the pre-match warmup. update() returns false the first time the snapshot
  // reports phase === "playing", at which point the overlay is removed and
  // its destroy() drops timers. The same client.onChat path also feeds the
  // overlay's chat log so the conversation carries through into the live
  // game (which keeps the chatLog this run also writes to).
  let warmupApi: ReturnType<typeof warmupOverlay> | null = null;
  if (matchmadeMode) {
    warmupApi = warmupOverlay({
      client,
      mode: matchmadeMode,
      initialWarmupEndsAt: matchmadeWarmupEndsAt,
    });
  }

  client.onSnapshot = (snap)    => {
    renderer.setSnapshot(snap);
    updateFromSnapshot(snap);
    if (warmupApi) {
      const stillWarmup = warmupApi.update(snap);
      if (!stillWarmup) {
        warmupApi.root.remove();
        warmupApi.destroy();
        warmupApi = null;
      }
    }
  };
  client.onChat     = (from, t) => {
    appendChat(from, t);
    warmupApi?.appendChat(from, t);
  };
  client.onAssist   = (text)    => appendChat("Builder", text);

  client.connect(gameId, team);

  // ── chat helper ──────────────────────────────────────────────────────────────
  function appendChat(from: string, text: string): void {
    const row = el("div", { class: "chat-row" },
      el("span", { class: "chat-from" }, from),
      document.createTextNode(" "),
      el("span", { class: "chat-text" }, text),
    );
    chatLog.append(row);
    while (chatLog.children.length > 40) chatLog.firstElementChild?.remove();
    chatLog.scrollTop = chatLog.scrollHeight;
  }

  // ── match banner ─────────────────────────────────────────────────────────────
  function matchTimerText(m: MatchState): string {
    if (m.phase === "ended") {
      const sec = Math.max(0, Math.ceil((m.restartAt - Date.now()) / 1000));
      return `Next match in ${sec}s`;
    }
    const remMs = Math.max(0, m.endsAt - Date.now());
    const mm    = Math.floor(remMs / 60000);
    const ss    = Math.floor((remMs % 60000) / 1000);
    return `${mm}:${ss.toString().padStart(2, "0")}`;
  }

  function showMatchBanner(snap: MergedWorldSnapshot): void {
    const winner  = snap.match.winnerId == null ? null : snap.players.find((p) => p.id === snap.match.winnerId);
    const hasTeams = snap.players.some((p) => p.team > 0);
    matchBanner.classList.remove("hidden");

    const title = el("div", { class: "match-title" },
      !winner            ? "Match over"
      : hasTeams && winner.team > 0 ? `Team ${winner.team} wins!`
      : `${winner.name} wins!`,
    );

    let rows: HTMLElement[];
    if (hasTeams) {
      const teams = new Map<number, { team: number; kills: number; deaths: number; players: PlayerState[] }>();
      for (const p of snap.players) {
        const entry = teams.get(p.team) ?? { team: p.team, kills: 0, deaths: 0, players: [] };
        entry.kills  += p.kills;
        entry.deaths += p.deaths;
        entry.players.push(p);
        teams.set(p.team, entry);
      }
      const teamList = [...teams.values()].sort((a, b) => b.kills - a.kills || a.deaths - b.deaths);
      rows = teamList.flatMap((t) => {
        const label  = t.team === 0 ? "No team" : `Team ${t.team}`;
        const winRow = winner && t.players.some((p) => p.id === winner.id);
        const header = el("tr", { class: winRow ? "winner team-header" : "team-header" },
          el("td", { style: `color: ${TEAM_COLORS[t.team] ?? "#ccc"}; font-weight: 600;` }, label),
          el("td", { class: "num" }, String(t.kills)),
          el("td", { class: "num" }, String(t.deaths)),
        );
        const members = t.players
          .slice().sort((a, b) => b.kills - a.kills || a.deaths - b.deaths)
          .map((p) => el("tr", {},
            el("td", { style: "padding-left: 1.2rem;" }, p.name),
            el("td", { class: "num" }, String(p.kills)),
            el("td", { class: "num" }, String(p.deaths)),
          ));
        return [header, ...members];
      });
    } else {
      const sorted = [...snap.players].sort((a, b) => b.kills - a.kills || a.deaths - b.deaths);
      rows = sorted.map((p) =>
        el("tr", { class: winner && p.id === winner.id ? "winner" : "" },
          el("td", {}, p.name),
          el("td", { class: "num" }, String(p.kills)),
          el("td", { class: "num" }, String(p.deaths)),
        ),
      );
    }

    matchBanner.replaceChildren(
      title,
      el("table", { class: "scoreboard" },
        el("thead", {},
          el("tr", {},
            el("th", {}, hasTeams ? "Team / Player" : "Player"),
            el("th", { class: "num" }, "K"),
            el("th", { class: "num" }, "D"),
          ),
        ),
        el("tbody", {}, ...rows),
      ),
    );
  }

  function hideMatchBanner(): void {
    if (!matchBanner.classList.contains("hidden")) matchBanner.classList.add("hidden");
  }

  // ── game loop ─────────────────────────────────────────────────────────────────
  let running = true;

  function frame(): void {
    if (!running) return;
    renderer.render(client.getSelfId());
    const cam = renderer.getCamera();
    input.setCamera(cam.x, cam.y, RENDER_SCALE);

    const hover = input.getHover();
    const tool  = input.getTool();
    if (hover && tool !== "none") {
      const entry = TOOLS.find((t) => t.id === tool);
      if (entry) renderer.renderOverlay(hover.tileX, hover.tileY, entry.color);
    }

    if (lastMatch && burgerOpen) {
      bmTimer.textContent = matchTimerText(lastMatch);
    }

    requestAnimationFrame(frame);
  }

  requestAnimationFrame(frame);

  const inputInterval = window.setInterval(() => {
    const s = input.sample();
    client.sendInput(s.throttle, s.turn, s.fire, s.laying, s.sightUp, s.sightDown);

    for (const a of input.drainActions()) {
      if (a.kind === "builder-front" && selfSnapshot?.alive) {
        const { tx, ty } = tileInFront(selfSnapshot);
        // Respect the selected tool so B key matches what mouse-click would do.
        // Falls back to "auto" (server picks based on terrain) when no tool is active.
        const bTool = input.getTool();
        const BUILDER_ACTION_MAP: Partial<Record<Tool, import("@webolo/shared").BuilderAction>> = {
          tree: "tree", road: "road", wall: "wall", mine: "mine",
        };
        client.sendBuilder(tx, ty, BUILDER_ACTION_MAP[bTool] ?? "auto");
      } else if (a.kind === "pillbox-drop-front") {
        client.sendPillbox("drop");
      } else if (a.kind === "spectate-cycle") {
        cycleSpectate(a.dir);
      } else if (a.kind === "toggle-scoreboard") {
        toggleScoreboard();
      } else if (a.kind === "map-click") {
        if (a.tool === "pillbox") {
          // Smart: repair a damaged friendly pillbox if one is at the tile,
          // otherwise drop a carried pillbox there.
          const pb = lastSnap?.pillboxes?.find(
            (p) => p.tileX === a.tileX && p.tileY === a.tileY && !p.dropped,
          );
          const allied = pb && selfSnapshot && (
            pb.owner === client.getSelfId() ||
            (selfSnapshot.team > 0 && pb.team === selfSnapshot.team)
          );
          if (pb && allied && pb.armor < PILLBOX_MAX_ARMOR) {
            client.sendBuilder(a.tileX, a.tileY, "repair");
          } else {
            client.sendPillbox("drop", a.tileX, a.tileY);
          }
        } else {
          // road + wall both double as boat-builders on water (server resolves).
          const BUILDER_MAP: Partial<Record<Tool, import("@webolo/shared").BuilderAction>> = {
            tree: "tree", road: "road", wall: "wall", mine: "mine",
          };
          const action = BUILDER_MAP[a.tool];
          if (action) client.sendBuilder(a.tileX, a.tileY, action);
        }
      }
    }
  }, 50);

  // ── assemble DOM ─────────────────────────────────────────────────────────────
  const canvasArea = el("div", { class: "game-canvas-area" });

  const wrap = el("div", { class: "game-wrap" },
    topBar,
    canvasArea,
    controlsModal,
    scoreboardModal,
  );
  if (warmupApi) wrap.append(warmupApi.root);

  // Close burger on outside click
  document.addEventListener("click", (e) => {
    if (burgerOpen && !burgerMenu.contains(e.target as Node) && e.target !== burgerBtn) {
      closeBurger();
    }
  });

  applyHudMode();
  updateHudButtons();
  renderer.setGraphicsMode(graphicsMode);
  updateGraphicsButtons();
  // Sync the Names toggle's active highlight with the loaded value.
  namesOnBtn.classList.toggle("active", showNames);
  namesOffBtn.classList.toggle("active", !showNames);

  // ── cleanup ───────────────────────────────────────────────────────────────────
  const observer = new MutationObserver((muts) => {
    for (const m of muts) {
      for (const removed of Array.from(m.removedNodes)) {
        if (removed === wrap || (removed as HTMLElement).contains?.(wrap)) {
          running = false;
          clearInterval(inputInterval);
          client.disconnect();
          input.destroy();
          observer.disconnect();
          return;
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  return wrap;
}

// ── helpers ───────────────────────────────────────────────────────────────────

function tileInFront(p: PlayerState): { tx: number; ty: number } {
  // Tile-step approach: advance exactly one tile in the facing direction.
  // The previous pixel-offset approach (tank.x + cos*1.5tiles) could land
  // on the tank's own tile for diagonal headings when the tank sits near
  // the "trailing" corner of its tile, causing the builder to build there.
  const ttx = Math.floor(p.x / TILE_SIZE);
  const tty = Math.floor(p.y / TILE_SIZE);
  const angle = (p.dir / TANK_DIRECTIONS) * Math.PI * 2 - Math.PI / 2;
  return {
    tx: ttx + Math.round(Math.cos(angle)),
    ty: tty + Math.round(Math.sin(angle)),
  };
}

function hbar(color: string): { wrap: HTMLElement; set: (pct: number, label: string) => void } {
  const fill = el("div", { class: "bar-fill", style: `background: ${color};` });
  const text = el("span", { class: "bar-text" }, "");
  const wrap = el("div", { class: "bar" }, fill, text);
  return {
    wrap,
    set(pct, label) {
      const p = Math.max(0, Math.min(1, pct));
      fill.style.width  = `${p * 100}%`;
      text.textContent  = label;
    },
  };
}

function vbar(color: string, label: string): { root: HTMLElement; set: (pct: number) => void } {
  const fill = el("div", { class: "vbar-fill", style: `background: ${color};` });
  const root = el("div", { class: "vbar" },
    fill,
    el("span", { class: "vbar-lbl" }, label),
  );
  return {
    root,
    set(pct) {
      fill.style.height = `${Math.max(0, Math.min(1, pct)) * 100}%`;
    },
  };
}

function bundle(label: string, b: HTMLElement): HTMLElement {
  return el("div", { class: "bar-row" }, el("span", { class: "pill" }, label), b);
}
